/**
 * we don't use @types/ol since it does not contain all functions in ol
 * use ol packages also make final file size more compact
 */
import i18n from "@/common/filters";

import Map from "ol/Map";
import Geometry from "ol/geom/Geometry";
import Point from "ol/geom/Point";
import LineString from "ol/geom/LineString";
import Polygon from "ol/geom/Polygon";
import Feature from "ol/Feature";
import Overlay from "ol/Overlay";
import OverlayPositioning from "ol/OverlayPositioning";
import { EventsKey } from "ol/events";

import { Draw } from "ol/interaction";

import { unByKey } from "ol/Observable";
import { IMeasureResult, Coordinate } from "../types";
import { Measure as MeasureStyle} from "../Style";
import { MultiPolygon } from 'ol/geom';

export class Drawer extends Draw {
  //following four are private in Control
  protected map: Map;
  protected sketch: Feature;
  private geomChangeEventKey_: EventsKey;
  private drawUndoEventKey_: EventsKey;

  private labels_: Array<Feature>;
  private labelIndex_: number; // IMPORTANT

  private drawTooltip_: Overlay;
  private drawTooltipElement_: HTMLElement;
  private drawTooltipCoordinate_: Coordinate;
  private drawingSource;

  constructor(options) {
    options = options || {};
    let drawingSource;
    if (options.drawingSource) {
      drawingSource = options.drawingSource;
      delete options.drawingSource
    }
    let afterInserted;
    if (options.afterInserted) {
      afterInserted = options.afterInserted;
      delete options.afterInserted;
    }

    super(options);

    this.addEvents();
    this.drawingSource = drawingSource;
    this.labelIndex_ = 0;
    this.undoRedo_ = (evt) => {
      this.undoRedo(evt);
    }
    if (afterInserted) {
      this.afterInserted = afterInserted;
    }
  }

  private get draw() : Draw {
    return (<Draw>this);
  }

  private addEvents() {
    this.draw.on('drawstart', this.startDraw);
    this.draw.on('drawend', this.endDraw);
  }

  private removeEvents() {
    this.draw.un('drawstart', this.startDraw);
    this.draw.un('drawend', this.endDraw);
  }

  private createDrawTooltip() {
    if (this.drawTooltipElement_) {
      this.drawTooltipElement_.parentNode.removeChild(this.drawTooltipElement_);
    }
    this.drawTooltipElement_ = document.createElement('div');
    this.drawTooltipElement_.className = 'tooltip tooltip-measure';
    this.drawTooltip_ = new Overlay({
      element: this.drawTooltipElement_,
      offset: [0, -15],
      positioning: OverlayPositioning.BOTTOM_CENTER
    });
    this.map.addOverlay(this.drawTooltip_);
  }

  private removeDrawTooltip() {
    if (this.drawTooltip_) {
      if (this.drawTooltipElement_) {
        this.drawTooltipElement_.parentNode.removeChild(this.drawTooltipElement_);
        this.drawTooltipElement_ = null;
      }
      if (this.map) {
        this.map.removeOverlay(this.drawTooltip_);
      }
      this.drawTooltip_ = null;
    }
  }

  startDraw(event) {
    this.sketch = event.feature || super['sketchFeature_'];
    let geom = this.sketch.getGeometry();
    let tooltipCoord = event.coordinate;
    let result: IMeasureResult;
    if (geom instanceof Point) {
      this.labels_ = null;
      this.createDrawTooltip();
      result = this.measurePoint(geom);
      tooltipCoord = geom.getLastCoordinate();
      this.drawTooltipElement_.innerHTML = result.content;
      this.drawTooltip_.setPosition(tooltipCoord);
    } else {
      if (geom instanceof Polygon) {
        this.labels_ = [null,null];
      } else if (geom instanceof LineString) {
        this.labels_ = [null];
      }
      let self = this;
      this.geomChangeEventKey_ = this.sketch.getGeometry().on('change', (evt) => {
        let geom = evt.target;
        if (geom instanceof Polygon) {
          self.measureArea();
          self.measureSegmentedDistance(geom);
        } else if (geom instanceof LineString) {
          result = self.measureDistance(geom);
          self.measureSegmentedDistance(geom);
          if (geom.getCoordinates().length>2) {
            if (!self.drawTooltip_) {
              self.createDrawTooltip();
            }
            self.drawTooltipElement_.innerHTML = result.content;
            tooltipCoord = geom.getLastCoordinate();
            self.drawTooltip_.setPosition(tooltipCoord);
          }
        }
      });
      let mapElem:Element = this.map.getTargetElement();
      mapElem.addEventListener('keyup', this.undoRedo_);
    }
  };

  endDraw(event) {
    if (this.sketch) {
      // this.sketch is null. endDraw() is called from this.remove()
      let geom = this.sketch.getGeometry();
      if (!(geom instanceof Point)) {
        unByKey(this.geomChangeEventKey_);
        let mapElem = this.map.getTargetElement();
        mapElem.removeEventListener('keyup', this.undoRedo_);
      }
      if (event.feature) {
        this.afterInserted(event.feature);
      }
    }
    this.removeDrawTooltip();
    this.drawingSource.clear();
    this.sketch = null;
    this.labels_ = null;
    this.labelIndex_ = 0;
    this.redoPoints = null;
  }

  /**
   * Undo or Redo
   * Undo when press esc or ctrl^Z key
   * Redo when press ctrl^Y
   * @param evt 
   */
  protected undoRedo_;
  protected discardDraw() {
    if (super['abortDrawing_']) {
      super['abortDrawing_']();
    }
    this.endDraw({feature: null});
  }
  
  private redoPoints:[[number,number]];
  undoRedo(event) {
    if (!this.sketch) {
      return;
    }
    let keyCode = event.keyCode || event.which;
    let ctrlKey = event.ctrlKey;
    if (keyCode==27) {
      this.discardDraw();
    } else if (ctrlKey) {
      let geom:Geometry = this.sketch.getGeometry();
      let coords:Array<Coordinate>;
      if (keyCode==90) { // ctrl^Z
        if (geom instanceof LineString) {
          coords = geom.getCoordinates();
          if (coords.length <= 2) {
            this.discardDraw();
            return;
          }
        } else if (geom instanceof Polygon) {
          coords = geom.getLinearRing(0).getCoordinates();
          if (coords.length <= 3) {
            this.discardDraw();
            return;
          }
        }
        let source = this.drawingSource;
        this.labelIndex_--;
        source.removeFeature(source.getFeatures()[this.labelIndex_ - this.labels_.length]);
        this.draw.removeLastPoint();
      } else if (keyCode==89) { // ctrl^Y

      }
    }
  }
  
  calcArea(geom:Polygon | MultiPolygon) : number {
    let sm = this.map['SOFTOO_MAP'];
    return sm.calcArea(geom);
  }
  
  calcLength(geom:LineString) : number {
    let sm = this.map['SOFTOO_MAP'];
    return sm.calcLength(geom);
  };
  
  measureArea(feature?:Feature) : IMeasureResult {
    feature = feature || this.sketch;
    feature.set('area_sqm', this.calcArea(<Polygon>feature.getGeometry()));
    let result: IMeasureResult;
    //if (this.unit_=='metric') {
    //  result = this.measureAreaMetric(geom,feature);
    //} else {
      result = this.measureAreaThai(feature);
    //}
    return result;
  }

  measureAreaMetric(feature:Feature) : IMeasureResult {
    let result: IMeasureResult;
    let area = feature.get('area_sqm');
    if (area > 10000) {
      result = {
        value: Math.round(area / 1000000 * 100) / 100,
        unit: i18n.L('unitSQKM')
      };
    } else {
      result = {
        value: Math.round(area * 100) / 100,
        unit: i18n.L('unitSQM')
      };
    }
    result.content = (<number>result.value).toThousandCommaString() + ' ' + result.unit;
    feature.set('labelStyle', {
      text: (result.value>0) ? result.content : '',
      font: '13px sans-serif',
      exceedLength: true,
      offsetX: 0,
      offsetY: 0
    });
    return result;
  }

  measureAreaThai(feature:Feature) : IMeasureResult {
    let area:number = feature.get('area_sqm');
    let rai:number,ngan:number,wah:number;
    area = area / 1600.0; //to rai
    rai = Math.trunc(area);
    area = (area - rai) * 4.0; //to ngan
    ngan = Math.trunc(area);
    area = (area - ngan) * 100.0; //to sqwah
    wah = Math.round(area);
    if (wah>=100) {
      wah -= 100;
      ngan += 1;
      if (ngan>=4) {
        ngan -= 1;
        rai += 1;
      }
    }

    let result: IMeasureResult;
    let units = [
      i18n.L('unitRai'),
      i18n.L('unitNgan'),
      i18n.L('unitWah')
    ];
    result = {
      value: [rai, ngan, wah],
      unit: units.join('-')
    }
    let vs = (<[number]>result.value).map((v)=>{
      return v.toThousandCommaString(0);
    });
    result.content = vs.join('-') + ' (' + result.unit + ')';
    
    let val:string[] = [];
    let unt:string[] = [];
    let i;
    for (i=0; i<3; i++) {
      if ((result.value[i]>0) || ((val.length>0) && i<2)) {
        val.push(result.value[i].toThousandCommaString(0));
        unt.push(units[i]);
      }
    }
    let label:string = '';
    if (val.length == 1) {
      label = val[0] + unt[0] ;
    } else if (val.length>0) {
      label = val.join('-') + '\n' + unt.join('-');
    }
    feature.set('labelStyle', {
      text: label,
      font: '13px sans-serif',
      exceedLength: true,
      offsetX: 0,
      offsetY: 0
    });
    return result;
  }

  measureDistance(geom:LineString) : IMeasureResult {
    let length = this.calcLength(geom);
    let result:IMeasureResult;
    if (length >= 1000) {
      result = {
        value: Math.round(length / 10) / 100,
        unit: i18n.L('unitKM')
      }
    } else {
      result = {
        value: Math.round(length * 100) / 100,
        unit: i18n.L('unitM')
      }
    }
    result.content = result.value + ' ' + result.unit;
    return result;
  }

  measureSegmentedDistance(geom:LineString | Polygon) {
    let labelIndex, coords:Array<Coordinate>;
    let labels = this.labels_;
    if (geom instanceof Polygon) {
      coords = geom.getLinearRing(0).getCoordinates();
    } else {
      coords = geom.getCoordinates();
    }
    labelIndex = coords.length - 1;
    if (labelIndex <= 0) {
      return;
    } else if (labelIndex>this.labelIndex_) {
      //new vertex has been added
      labels[0] = labels[labels.length-1];
      labels[labels.length-1] = null;
      this.labelIndex_ = labelIndex;
    }

    coords.splice(0,(coords.length - (labels.length + 1)));
    let p1:Coordinate, p2:Coordinate;
    let rad:number, dx:number, dy:number;
    let line: LineString;
    let i;
    for (i=0; i<labels.length; i++) {
      p1 = coords[i];
      p2 = coords[i + 1];
      line = new LineString([p1,p2]);
      dx = p2[0] - p1[0];
      dy = p2[1] - p1[1];
      if (dy==0) {
          rad = 0;
      } else if (dx==0) {
          rad = -Math.PI / 2;
      } else {
          rad = -Math.atan2(dy,dx);
          if (dx < 0) {
              rad += Math.PI;
          }
      }
      let result: IMeasureResult = this.measureDistance(line);
      if (result.value > 0) {
        if (!labels[i]) {
          labels[i] = new Feature({
            geometry: line,
            LABEL_ONLY: true,
            labelStyle: {
              text: result.content,
              rotation: rad,
              font: '13px sans-serif',
              offsetY: -10
            }
          });
          labels[i].setStyle(MeasureStyle);
          this.drawingSource.addFeature(labels[i]);
        } else {
          labels[i].setStyle(MeasureStyle);
          labels[i].setGeometry(line);
          labels[i].set('labelStyle', {
            text: result.content,
            rotation: rad,
            font: '13px sans-serif',
            offsetY: -10
          });
        }
      }
    }
  }

  measurePoint(geom:Point) : IMeasureResult {
    let p:Point = <Point>geom.clone();
    let mPrj = this.map.getView().getProjection().getCode();
    p.transform(mPrj, 'EPSG:4326');
    let coords = p.getLastCoordinate();
    let result:IMeasureResult;
    result = {
      value: coords,
      unit: 'WGS84',
      content: coords[0] + ',' + coords[1]
    };
    return result;
  }

  afterInserted(feature:Feature) {
    // to be override by application
    return true;
  }

  setMap(map:Map) {
    if (map) {
      this.map = map;
      super.setMap(map);
      //map['SOFTOO_MAP'].createSnap();
    }
  }
  
  remove() {
    if (this.map) {
      this.draw.getOverlay().getSource().clear();
      this.removeEvents();
      this.discardDraw();
      this.map.removeInteraction(this);
    }
  }
}